import React, { useState, useEffect, useRef, useCallback } from 'react';
import { normalizeSnilsDigits, formatSnils, hasManualSnilsDash } from '../../utils/snils';
import { applyPhoneMask, formatPhone, getCaretPosition, normalizePhoneDigits } from '../../utils/phoneMask';
import OwnerBadge from '../Common/OwnerBadge';
import SearchableSelect from '../Common/SearchableSelect';
import { apiService } from '../../services/api';
import Toast from '../Common/Toast';

const OrganizerForm = ({ 
  organizer, 
  formData, 
  setFormData, 
  onSave, 
  onCancel, 
  isViewMode = false,
  onFormChange,
  fixedType = null
}) => {
  const isManager = fixedType === 'arbitrage_manager';
  const [initialFormData, setInitialFormData] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [innFillStatus, setInnFillStatus] = useState({ loading: false, error: '' });
  const [innFillTaskId, setInnFillTaskId] = useState('');
  const innFillTaskIdRef = useRef('');
  const [innTypePrompt, setInnTypePrompt] = useState({ open: false, inn: '' });
  const [innFillProgress, setInnFillProgress] = useState(0);
  const [innFillTarget, setInnFillTarget] = useState(0);
  const [innFillStage, setInnFillStage] = useState('Подготовка');
  const [showInnFillProgress, setShowInnFillProgress] = useState(false);
  const [innFillNotice, setInnFillNotice] = useState('');
  const innFillStreamRef = useRef(null);
  const innFillPollRef = useRef(null);
  const [autoFilledFields, setAutoFilledFields] = useState(() => new Set());
  const phoneInputRef = useRef(null);
  const snilsInputRef = useRef(null);
  const innInputRef = useRef(null);
  const [sros, setSros] = useState([]);
  const [srosLoading, setSrosLoading] = useState(false);
  const [selectedSroId, setSelectedSroId] = useState('');
  const [innGuideActive, setInnGuideActive] = useState(true);
  const [innGuideToast, setInnGuideToast] = useState(null);
  const hasInnValue = Boolean(String(formData?.inn || '').trim());
  const shouldHighlightInn = !isViewMode && innGuideActive && !hasInnValue;

  const loadSros = useCallback(async () => {
    try {
      setSrosLoading(true);
      const all = [];
      let page = 1;
      while (true) {
        const resp = await apiService.getSros(`?page=${page}`);
        const chunk = resp.data.results || [];
        all.push(...chunk);
        if (!resp.data.next) break;
        page += 1;
        if (page > 200) break;
      }
      setSros(all);
    } catch (e) {
      // Если справочник СРО временно недоступен — форма всё равно должна работать
      setSros([]);
    } finally {
      setSrosLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isManager) return;
    loadSros();
  }, [loadSros, isManager]);

  useEffect(() => {
    setInnGuideActive(true);
  }, [organizer?.id, fixedType]);

  useEffect(() => {
    if (!isViewMode && innGuideActive && !hasInnValue) {
      setInnGuideToast({
        type: 'success',
        message: 'Рекомендуемый порядок: введите ИНН и нажмите «Заполнить по ИНН».'
      });
      return;
    }
    setInnGuideToast(null);
  }, [isViewMode, innGuideActive, hasInnValue]);

  const dismissInnGuide = () => {
    if (innGuideActive) setInnGuideActive(false);
    if (innGuideToast) setInnGuideToast(null);
  };

  useEffect(() => {
    if (isViewMode || !innGuideActive || hasInnValue) return;
    const timer = setTimeout(() => {
      if (innInputRef.current && document.activeElement !== innInputRef.current) {
        innInputRef.current.focus();
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [isViewMode, innGuideActive, hasInnValue]);

  useEffect(() => {
    if (!innFillStatus.loading) {
      if (showInnFillProgress) {
        setInnFillStage('Готово');
        setInnFillProgress(100);
        setInnFillTarget(100);
        const closeTimer = setTimeout(() => {
          setShowInnFillProgress(false);
          setInnFillProgress(0);
          setInnFillTarget(0);
          setInnFillStage('Подготовка');
        }, 150);
        return () => clearTimeout(closeTimer);
      }
      if (innFillStreamRef.current) {
        innFillStreamRef.current.close();
        innFillStreamRef.current = null;
      }
      if (innFillPollRef.current) {
        clearInterval(innFillPollRef.current);
        innFillPollRef.current = null;
      }
      return;
    }
    let isActive = true;
    setShowInnFillProgress(true);
    setInnFillProgress(0);
    setInnFillStage('Подготовка');

    if (innFillStreamRef.current) {
      innFillStreamRef.current.close();
      innFillStreamRef.current = null;
    }
    if (innFillPollRef.current) {
      clearInterval(innFillPollRef.current);
      innFillPollRef.current = null;
    }

    const startPolling = () => {
      if (innFillPollRef.current) return;
      const pollOnce = async () => {
        try {
          const taskId = innFillTaskIdRef.current || innFillTaskId;
          if (!taskId) return;
          const resp = isManager
            ? await apiService.getManagerInnFillStatus(taskId)
            : await apiService.getOrganizerInnFillStatus(taskId);
          const payload = resp?.data || {};
          if (payload.stage) setInnFillStage(payload.stage);
          if (Number.isFinite(payload.percent)) {
            const percent = Math.max(0, Math.min(100, Math.round(payload.percent)));
            setInnFillTarget(percent);
          }
        } catch (_) {}
      };
      pollOnce();
      innFillPollRef.current = setInterval(pollOnce, 700);
    };

    (async () => {
      const token = await apiService.ensureAccessToken();
      if (!token) {
        startPolling();
        return;
      }
      const streamUrl = isManager
        ? apiService.getManagerInnFillStreamUrl(token)
        : apiService.getOrganizerInnFillStreamUrl(token);
      const es = new EventSource(streamUrl);
      innFillStreamRef.current = es;
      es.onmessage = (event) => {
        if (!isActive) return;
        try {
          const payload = JSON.parse(event.data || '{}');
          const taskId = innFillTaskIdRef.current || innFillTaskId;
          if (!taskId) return;
          if (payload.task_id && payload.task_id !== taskId) return;
          if (payload.stage) setInnFillStage(payload.stage);
          if (Number.isFinite(payload.percent)) {
            const percent = Math.max(0, Math.min(100, Math.round(payload.percent)));
            setInnFillTarget(percent);
          }
        } catch (_) {}
      };
      es.onerror = () => {
        if (innFillStreamRef.current) {
          innFillStreamRef.current.close();
          innFillStreamRef.current = null;
        }
      };
      startPolling();
    })();

    return () => {
      isActive = false;
      if (innFillStreamRef.current) {
        innFillStreamRef.current.close();
        innFillStreamRef.current = null;
      }
      if (innFillPollRef.current) {
        clearInterval(innFillPollRef.current);
        innFillPollRef.current = null;
      }
    };
  }, [innFillStatus.loading, innFillTaskId]);

  useEffect(() => {
    if (!innFillStatus.loading) return;
    if (innFillProgress >= innFillTarget) return;
    const timer = setInterval(() => {
      setInnFillProgress((prev) => {
        if (prev >= innFillTarget) return prev;
        return Math.min(prev + 1, innFillTarget);
      });
    }, 120);
    return () => clearInterval(timer);
  }, [innFillStatus.loading, innFillTarget, innFillProgress]);

  const formatSroInfoLine = (payload) => {
    const name = (payload?.name || '').trim();
    const inn = (payload?.inn || '').trim();
    const ogrn = (payload?.ogrn || '').trim();
    const address = (payload?.address || '').trim();
    const parts = [
      name || null,
      inn ? `ИНН: ${inn}` : null,
      ogrn ? `ОГРН: ${ogrn}` : null,
      address ? `Адрес: ${address}` : null,
    ].filter(Boolean);
    return parts.join(', ');
  };

  const stripEntrepreneurPrefix = (value) => String(value || '')
    .replace(/^\s*(?:ИП|Индивидуальный\s+предприниматель)\s+/iu, '')
    .trim();

  const detectEntrepreneurFromImportPayload = (payload) => {
    const detectedEntityType = String(payload?.detected_entity_type || '').trim().toLowerCase();
    if (detectedEntityType === 'entrepreneur') return true;
    const legalForm = String(payload?.legal_form || '').trim().toUpperCase();
    const rawName = String(payload?.name || '').trim();
    const ogrnDigits = String(payload?.ogrn || '').replace(/\D/g, '');
    return (
      legalForm === 'ИП' ||
      /^\s*(?:ИП|Индивидуальный\s+предприниматель)\b/iu.test(rawName) ||
      ogrnDigits.length === 15
    );
  };

  const handleSelectSro = (id) => {
    setSelectedSroId(id || '');
    if (autoFilledFields.has('sro_select')) {
      setAutoFilledFields(prev => {
        const next = new Set(prev);
        next.delete('sro_select');
        next.delete('sro_name');
        next.delete('sro_inn');
        next.delete('sro_ogrn');
        next.delete('sro_address');
        next.delete('sro_info');
        return next;
      });
    }
    const sro = (sros || []).find(x => String(x.id) === String(id));
    if (!sro) return;
    const shortName = (sro.short_name || '').trim();
    const displayName = shortName || sro.name || '';
    const oneLine = formatSroInfoLine({ name: displayName, inn: sro.inn, ogrn: sro.ogrn, address: sro.address });
    setFormData(prev => ({
      ...(prev || {}),
      sro_name: displayName,
      sro_inn: sro.inn || '',
      sro_ogrn: sro.ogrn || '',
      sro_address: sro.address || '',
      sro_info: oneLine,
    }));
  };

  // Инициализация формы
  useEffect(() => {
    let newFormData;

    if (organizer) {
      const orgType = fixedType || organizer.type || 'arbitrage_manager';
      const organizerOgrnDigits = String(organizer.ogrn || '').replace(/\D/g, '');
      const inferEntrepreneur = (
        orgType === 'specialized_org' && (
          String(organizer.legal_form || '').trim().toUpperCase() === 'ИП' ||
          organizerOgrnDigits.length === 15
        )
      );
      const derivedEntityType = orgType === 'arbitrage_manager'
        ? 'individual'
        : (inferEntrepreneur ? 'entrepreneur' : 'legal');
      const entityType = fixedType === 'arbitrage_manager'
        ? 'individual'
        : (organizer.entity_type || derivedEntityType);
      const existingSnilsDigits = normalizeSnilsDigits(organizer.snils || '');
      const existingSnilsFormatted = orgType === 'arbitrage_manager'
        ? formatSnils(existingSnilsDigits, hasManualSnilsDash(organizer.snils || ''))
        : (organizer.snils || '');
      newFormData = {
        ...organizer,
        type: orgType,
        entity_type: entityType,
        legal_form: entityType === 'entrepreneur' ? 'ИП' : (organizer.legal_form || ''),
        phone: formatPhone(normalizePhoneDigits(organizer.phone || '')),
        snils: existingSnilsFormatted
      };
    } else {
      newFormData = {
        name: '',
        type: fixedType || 'arbitrage_manager',
        entity_type: fixedType === 'arbitrage_manager' ? 'individual' : 'legal',
        legal_form: '',
        inn: '',
        ogrn: '',
        kpp: '',
        address: '',
        phone: '',
        email: '',
        mailing_address: '',
        executive_role: '',
        executive_name: '',
        contacts: '',
        sro_info: '',
        sro_name: '',
        sro_inn: '',
        sro_ogrn: '',
        sro_address: '',
        snils: ''
      };
    }

    setFormData(newFormData);
    setInitialFormData(JSON.parse(JSON.stringify(newFormData)));
    setValidationErrors({});
    setAutoFilledFields(new Set());
  }, [organizer, setFormData]);

  // Отслеживание изменений в форме
  useEffect(() => {
    if (initialFormData) {
      const changed = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      if (onFormChange) {
        onFormChange(changed);
      }
    }
  }, [formData, initialFormData, onFormChange]);

  function sanitizeDigits(value, maxLength) {
    return value.replace(/\D/g, '').slice(0, maxLength);
  }

  const handlePhoneChange = (event) => {
    if (isViewMode) return;
    const { value, selectionStart } = event.target;
    const { formatted, caret } = applyPhoneMask(value, selectionStart);

    setFormData(prev => ({ ...(prev || {}), phone: formatted }));
    requestAnimationFrame(() => {
      const input = phoneInputRef.current;
      if (!input) return;
      input.setSelectionRange(caret, caret);
    });
  }

  const handleSnilsChange = (event) => {
    if (isViewMode) return;
    const { value, selectionStart } = event.target;
    const digits = normalizeSnilsDigits(value);
    const useDashAfterNine = hasManualSnilsDash(value);
    const formatted = formatSnils(digits, useDashAfterNine);
    const digitsBeforeCaret = normalizeSnilsDigits(value.slice(0, selectionStart ?? value.length));
    const digitsBeforeCaretCount = digitsBeforeCaret.length;
    setFormData(prev => ({ ...(prev || {}), snils: formatted }));
    requestAnimationFrame(() => {
      const input = snilsInputRef.current;
      if (!input) return;
      let caret = getCaretPosition(formatted, digitsBeforeCaretCount, formatted.length);
      if (
        useDashAfterNine &&
        digits.length === digitsBeforeCaretCount &&
        formatted.endsWith('-')
      ) {
        caret = formatted.length;
      }
      input.setSelectionRange(caret, caret);
    });
  };

  const getInnLength = () => 12;

  const getOgrnLength = (entityType) => {
    if (entityType === 'legal') return 13;
    if (entityType === 'entrepreneur') return 15;
    return 0;
  };
  const updateFormData = (field, value) => {
    const currentType = field === 'type' ? value : (formData?.type || fixedType || 'arbitrage_manager');
    const currentEntityType = currentType === 'arbitrage_manager'
      ? 'individual'
      : (field === 'entity_type' ? value : (formData?.entity_type || 'legal'));

    if (autoFilledFields.has(field)) {
      setAutoFilledFields(prev => {
        const next = new Set(prev);
        next.delete(field);
        return next;
      });
    }
    if (innFillNotice && (field === 'inn' || field === 'entity_type' || field === 'type')) {
      setInnFillNotice('');
    }

    let nextValue = value;
    switch (field) {
      case 'inn':
        nextValue = sanitizeDigits(value, getInnLength());
        break;
      case 'kpp':
        nextValue = (currentType === 'specialized_org' && currentEntityType === 'legal') ? sanitizeDigits(value, 9) : '';
        break;
      case 'ogrn':
        nextValue = (currentType === 'specialized_org' && (currentEntityType === 'legal' || currentEntityType === 'entrepreneur'))
          ? sanitizeDigits(value, getOgrnLength(currentEntityType))
          : '';
        break;
      case 'snils':
        nextValue = currentType === 'arbitrage_manager' ? formatSnils(normalizeSnilsDigits(value), hasManualSnilsDash(value)) : '';
        break;
      case 'phone':
        nextValue = formatPhone(normalizePhoneDigits(value));
        break;
      default:
        break;
    }

    const updated = formData ? { ...formData } : {};
    updated[field] = nextValue;

    if (field === 'type' || field === 'entity_type') {
      updated.inn = sanitizeDigits(updated.inn || '', getInnLength());
      if (currentEntityType === 'entrepreneur') {
        updated.legal_form = 'ИП';
      } else if (currentEntityType === 'individual') {
        updated.legal_form = '';
      }

      // ОГРН/КПП только для спецорганизаций (карточка организатора торгов)
      if (currentType !== 'specialized_org') {
        updated.entity_type = currentType === 'arbitrage_manager' ? 'individual' : (updated.entity_type || 'legal');
        updated.kpp = '';
        updated.ogrn = '';
      } else {
        if (currentEntityType !== 'legal') {
          updated.kpp = '';
          updated.executive_name = '';
          updated.address = '';
          updated.executive_role = '';
        } else {
        }
        if (currentEntityType === 'individual') {
          updated.ogrn = '';
        } else {
          updated.ogrn = sanitizeDigits(updated.ogrn || '', getOgrnLength(currentEntityType));
        }
        if (!updated.entity_type) {
          updated.entity_type = 'legal';
        }
      }

      // СНИЛС только для управляющих (type=arbitrage_manager)
      if (currentType !== 'arbitrage_manager') {
        updated.snils = '';
      } else {
        const existingDigits = normalizeSnilsDigits(updated.snils || '');
        updated.snils = formatSnils(existingDigits, hasManualSnilsDash(updated.snils || ''));
      }

      updated.phone = formatPhone(normalizePhoneDigits(updated.phone || ''));
    }

    setFormData(updated);
  };

  const validateForm = () => {
    const errors = {};
    const currentType = fixedType || formData?.type || 'arbitrage_manager';
    const currentEntityType = currentType === 'arbitrage_manager'
      ? 'individual'
      : (formData?.entity_type || 'legal');

    if (!formData?.name || !formData.name.trim()) {
      errors.name = 'Обязательное поле';
    }

    if (!currentType) {
      errors.type = 'Выберите тип организатора';
    }
    if (currentType === 'specialized_org' && !currentEntityType) {
      errors.entity_type = 'Выберите тип организатора';
    }

    const cleanINN = (formData?.inn || '').replace(/\D/g, '');
    if (cleanINN) {
      if (!(cleanINN.length === 10 || cleanINN.length === 12)) {
        errors.inn = 'ИНН должен содержать 10 или 12 цифр';
      }
      if (currentEntityType === 'legal' && cleanINN.length === 12) {
        errors.inn = 'ИНН организатора превышает длину для юридического лица';
      }
      if ((currentEntityType === 'individual' || currentEntityType === 'entrepreneur') && cleanINN.length === 10) {
        errors.inn = `ИНН организатора недостаточной длины для ${currentEntityType === 'individual' ? 'физического лица' : 'ИП'}`;
      }
    }

    if (currentType === 'specialized_org') {
      const cleanKPP = (formData?.kpp || '').replace(/\D/g, '');
      if (currentEntityType === 'legal' && cleanKPP && cleanKPP.length !== 9) {
        errors.kpp = 'КПП должен содержать 9 цифр';
      }

      const cleanOGRN = (formData?.ogrn || '').replace(/\D/g, '');
      if (cleanOGRN) {
        if (currentEntityType === 'legal' && cleanOGRN.length !== 13) {
          errors.ogrn = 'ОГРН должен содержать 13 цифр';
        }
        if (currentEntityType === 'entrepreneur' && cleanOGRN.length !== 15) {
          errors.ogrn = 'ОГРНИП должен содержать 15 цифр';
        }
      }
    }

    if (currentType === 'arbitrage_manager') {
      const cleanSnils = (formData?.snils || '').replace(/\D/g, '');
      if (cleanSnils && cleanSnils.length !== 11) {
        errors.snils = 'СНИЛС должен содержать 11 цифр';
      }
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Неверный формат email';
    }

    const cleanPhone = (formData?.phone || '').replace(/\D/g, '');
    if (formData?.phone && cleanPhone.length < 10) {
      errors.phone = 'Номер телефона должен содержать не менее 10 цифр';
    }

    return errors;
  };

  const runSave = async (shouldExit = false) => {
    if (isViewMode || isSaving) return false;
    const errors = validateForm();
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return false;
    }

    if (!onSave) return false;

    setIsSaving(true);
    try {
      const payload = { ...(formData || {}) };
      if (!payload.executive_role || !String(payload.executive_role).trim()) {
        payload.executive_role = 'Генеральный директор';
      }
      // Сведения о СРО храним одной строкой (для совместимости со старым полем),
      // но только для карточки управляющего. Для организатора это поле скрыто и не должно затираться.
      if (isManager) {
        payload.sro_info = formatSroInfoLine({
          name: payload.sro_name,
          inn: payload.sro_inn,
          ogrn: payload.sro_ogrn,
          address: payload.sro_address,
        });
      }
      const result = await onSave(payload, { exit: shouldExit });

      if (result === false) {
        return false;
      }

      const snapshot = JSON.parse(JSON.stringify(payload));
      setInitialFormData(snapshot);
      setValidationErrors({});
      if (onFormChange) {
        onFormChange(false);
      }
      return result;
    } catch (error) {
      console.error('Ошибка при сохранении организатора:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    runSave(false).catch(() => {});
  };

  const pollInnResult = async (taskId, isManagerTask) => {
    const startAt = Date.now();
    const maxWaitMs = 8 * 60 * 1000;
    while (Date.now() - startAt < maxWaitMs) {
      let resp;
      try {
        resp = isManagerTask
          ? await apiService.getManagerInnFillResult(taskId)
          : await apiService.getOrganizerInnFillResult(taskId);
      } catch (err) {
        const pending = err.response?.status === 202;
        if (!pending) {
          throw err;
        }
      }
      const payload = resp?.data || {};
      if (payload.status === 'ready') {
        const data = payload.data || {};
        const hasContent = Object.values(data || {}).some((value) => {
          if (value === null || value === undefined) return false;
          return String(value).trim() !== '';
        });
        if (!hasContent) {
          throw new Error('Такой ИНН не найден');
        }
        return data;
      }
      if (payload.status === 'error') {
        const rawMessage = payload.error || 'Не удалось заполнить данные по ИНН.';
        const message = /не\\s+найден|ничего\\s+не\\s+найдено/i.test(rawMessage)
          ? 'Такой ИНН не найден'
          : rawMessage;
        throw new Error(message);
      }
      await new Promise((resolve) => setTimeout(resolve, 800));
    }
    throw new Error('Импорт занял слишком много времени. Попробуйте позже.');
  };

  const runFillByInn = async (overrideEntityType = null) => {
    if (isViewMode || innFillStatus.loading) return;
    const innValue = formData?.inn || '';
    const cleanInn = innValue.replace(/\D/g, '');
    if (!cleanInn) {
      setInnFillStatus({ loading: false, error: 'Введите ИНН для заполнения.' });
      return;
    }
    if (!(cleanInn.length === 10 || cleanInn.length === 12)) {
      setInnFillStatus({ loading: false, error: 'ИНН должен содержать 10 или 12 цифр.' });
      return;
    }
    if (!isManager) {
      const currentType = overrideEntityType || formData?.entity_type || 'legal';
      if (currentType === 'legal' && cleanInn.length === 12) {
        setInnTypePrompt({ open: true, inn: cleanInn });
        return;
      }
      if ((currentType === 'individual' || currentType === 'entrepreneur') && cleanInn.length === 10) {
        setFormData((prev) => ({ ...(prev || {}), entity_type: 'legal' }));
        await runFillByInn('legal');
        return;
      }
    }
    setInnFillNotice('');
    setInnFillStatus({ loading: true, error: '' });
    try {
      const requestedEntityType = overrideEntityType || formData?.entity_type || 'legal';
      const response = isManager
        ? await apiService.fillManagerByInn({ inn: cleanInn })
        : await apiService.fillOrganizerByInn({
            inn: cleanInn,
            entity_type: requestedEntityType
          });
      const taskId = response?.data?.task_id;
      if (!taskId) {
        throw new Error('Не удалось запустить импорт по ИНН.');
      }
      innFillTaskIdRef.current = taskId;
      setInnFillTaskId(taskId);
      const data = await pollInnResult(taskId, isManager);
      const rawPhoneDigits = String(data.phone || '').replace(/\D/g, '');
      const innDigits = String(cleanInn || '').replace(/\D/g, '');
      let phoneDigits = rawPhoneDigits;
      // Защита: не подставляем "телефон", если это ИНН или его фрагмент.
      if (
        phoneDigits &&
        innDigits &&
        (phoneDigits === innDigits || phoneDigits.includes(innDigits) || innDigits.includes(phoneDigits))
      ) {
        phoneDigits = '';
      }
      if (phoneDigits.length === 11 && (phoneDigits.startsWith('7') || phoneDigits.startsWith('8'))) {
        phoneDigits = phoneDigits.slice(1);
      }
      if (phoneDigits && phoneDigits.length > 11) {
        const match = phoneDigits.match(/(?:7)\d{10}/g);
        if (match && match.length > 0) {
          phoneDigits = match[0];
        }
      }
      if (phoneDigits && phoneDigits.length > 10) {
        phoneDigits = phoneDigits.slice(-10);
      }
      if (
        phoneDigits &&
        innDigits &&
        (phoneDigits === innDigits || phoneDigits.includes(innDigits) || innDigits.includes(phoneDigits))
      ) {
        phoneDigits = '';
      }
      const formattedPhone = phoneDigits ? formatPhone(phoneDigits) : '';
      const snilsDigits = normalizeSnilsDigits(data.snils || '');
      const snilsFormatted = snilsDigits ? formatSnils(snilsDigits, hasManualSnilsDash(data.snils || '')) : '';

      const nextSroName = (data.sro_name || '').trim();
      const nextSroInn = (data.sro_inn || '').trim();
      const nextSroOgrn = (data.sro_ogrn || '').trim();
      const shouldAutoSwitchToEntrepreneur = (
        !isManager &&
        requestedEntityType === 'legal' &&
        cleanInn.length === 10 &&
        detectEntrepreneurFromImportPayload(data)
      );
      const effectiveEntityType = shouldAutoSwitchToEntrepreneur
        ? 'entrepreneur'
        : requestedEntityType;
      const isEffectiveEntrepreneur = effectiveEntityType === 'entrepreneur';
      const normalizedImportedName = isEffectiveEntrepreneur
        ? stripEntrepreneurPrefix(data.name || '')
        : (data.name || '');
      if (shouldAutoSwitchToEntrepreneur) {
        setInnFillNotice(
          'Федресурс вернул карточку ИП. Тип организатора автоматически переключен на ИП. После импорта проверьте и допишите недостающие цифры ИНН и ОГРНИП по официальной выписке ЕГРИП.'
        );
      }
      const sroId = data.sro_id || '';
      let resolvedSroId = sroId || '';
      if (isManager && !resolvedSroId && (nextSroName || nextSroInn || nextSroOgrn) && Array.isArray(sros) && sros.length > 0) {
        const normalizeName = (value) => (value || '')
          .toLowerCase()
          .replace(/ё/g, 'е')
          .replace(/[«»"]/g, '')
          .replace(/[^\p{L}\p{N}\s]/gu, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        const normalizeShort = (value) => normalizeName(value)
          .replace(/\b(сро|саморегулируемая|организация|ассоциация)\b/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        const toAcronym = (value) => {
          const tokens = normalizeName(value).split(' ').filter(Boolean);
          return tokens.map((word) => word[0]).join('');
        };
        const compact = (value) => normalizeName(value).replace(/\s+/g, '');
        const matchedCandidates = sros.filter((item) => {
          if (nextSroInn || nextSroOgrn) {
            if (nextSroInn && String(item.inn || '').trim() !== nextSroInn) return false;
            if (nextSroOgrn && String(item.ogrn || '').trim() !== nextSroOgrn) return false;
            return true;
          }
          if (nextSroName) {
            const left = normalizeName(item.name);
            const right = normalizeName(nextSroName);
            if (left === right) return true;
            const leftShort = normalizeShort(item.name);
            const rightShort = normalizeShort(nextSroName);
            if (leftShort && rightShort && (leftShort === rightShort)) return true;
            if (left.includes(right) || right.includes(left)) return true;
            if (leftShort && rightShort && (leftShort.includes(rightShort) || rightShort.includes(leftShort))) return true;
            const leftAcr = toAcronym(item.name);
            const rightCmp = compact(nextSroName);
            if (leftAcr && rightCmp && (leftAcr === rightCmp || leftAcr.includes(rightCmp) || rightCmp.includes(leftAcr))) return true;
            const shortValue = normalizeName(item.short_name || '');
            const shortAlt = normalizeShort(item.short_name || '');
            if (shortValue && (shortValue === right || right.includes(shortValue) || shortValue.includes(right))) return true;
            if (shortAlt && rightShort && (shortAlt === rightShort || shortAlt.includes(rightShort) || rightShort.includes(shortAlt))) return true;
            return false;
          }
          return false;
        });
        if (matchedCandidates.length === 1) {
          resolvedSroId = matchedCandidates[0].id;
        }
      }

      if (isManager) {
        if (resolvedSroId) {
          handleSelectSro(resolvedSroId);
        }
      }

      const filled = new Set();
      if (data.name) filled.add('name');
      if (cleanInn) filled.add('inn');
      if (snilsFormatted && isManager) filled.add('snils');
      if (data.email) filled.add('email');
      if (formattedPhone) filled.add('phone');
      if (data.mailing_address) filled.add('mailing_address');
      if (data.address) filled.add('address');
      if (!isManager) {
        if (data.ogrn) filled.add('ogrn');
        if (data.kpp) filled.add('kpp');
        if (data.legal_form) filled.add('legal_form');
        if (data.executive_role) filled.add('executive_role');
        if (data.executive_name) filled.add('executive_name');
      }
      if (isManager && resolvedSroId) {
        filled.add('sro_select');
        filled.add('sro_name');
        filled.add('sro_inn');
        filled.add('sro_ogrn');
        filled.add('sro_address');
        filled.add('sro_info');
      } else if (isManager && (nextSroName || nextSroInn || nextSroOgrn)) {
        filled.add('sro_name');
        if (nextSroInn) filled.add('sro_inn');
        if (nextSroOgrn) filled.add('sro_ogrn');
      }
      setAutoFilledFields(filled);

      setFormData(prev => ({
        ...(prev || {}),
        entity_type: isManager ? (prev?.entity_type || 'individual') : effectiveEntityType,
        name: normalizedImportedName || prev?.name || '',
        legal_form: isManager ? (prev?.legal_form || '') : (isEffectiveEntrepreneur ? 'ИП' : (data.legal_form || prev?.legal_form || '')),
        inn: cleanInn || prev?.inn || '',
        snils: isManager ? (snilsFormatted || prev?.snils || '') : (prev?.snils || ''),
        email: data.email || prev?.email || '',
        phone: formattedPhone || prev?.phone || '',
        mailing_address: data.mailing_address || prev?.mailing_address || '',
        address: data.address || prev?.address || '',
        sro_name: isManager ? (nextSroName || prev?.sro_name || '') : (prev?.sro_name || ''),
        sro_inn: isManager ? (nextSroInn || prev?.sro_inn || '') : (prev?.sro_inn || ''),
        sro_ogrn: isManager ? (nextSroOgrn || prev?.sro_ogrn || '') : (prev?.sro_ogrn || ''),
        ogrn: data.ogrn || prev?.ogrn || '',
        kpp: data.kpp || prev?.kpp || '',
        executive_role: data.executive_role || prev?.executive_role || '',
        executive_name: data.executive_name || prev?.executive_name || ''
      }));
      if (isManager && !resolvedSroId && nextSroName) {
        setSelectedSroId('');
        setInnFillStatus({
          loading: false,
          error: `СРО не найдено в справочнике: ${nextSroName}`
        });
      } else {
        setInnFillStatus({ loading: false, error: '' });
      }
    } catch (err) {
      const rawMessage = err.response?.data?.error || err.message || 'Не удалось заполнить данные по ИНН.';
      const message = /не\\s+найден|ничего\\s+не\\s+найдено/i.test(rawMessage)
        ? 'Такой ИНН не найден'
        : rawMessage;
      setInnFillStatus({ loading: false, error: message });
    }
  };


  const renderField = ({ label, name, type = 'text', required = false, options = [], placeholder, rows, marginBottom = 20, autoFilled = false, disabled = false }) => {
    const error = validationErrors[name];
    const fieldStyle = {
      marginBottom: `${marginBottom}px`
    };

    const labelStyle = {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      color: '#2c3e50',
      fontSize: '14px'
    };

    const inputStyle = {
      width: '100%',
      padding: '12px',
      border: error ? '2px solid #e74c3c' : '1px solid #ddd',
      borderRadius: '6px',
      fontSize: '14px',
      fontFamily: 'inherit',
      backgroundColor: (isViewMode || disabled) ? '#f8f9fa' : 'white',
      color: (isViewMode || disabled) ? '#6c757d' : '#2c3e50',
      outline: 'none',
      transition: 'border-color 0.2s ease',
      boxShadow: autoFilled ? 'inset 4px 0 0 #1f6feb' : 'none'
    };

    const errorStyle = {
      color: '#e74c3c',
      fontSize: '12px',
      marginTop: '4px',
      display: 'block'
    };

    return (
      <div
        style={{
          ...fieldStyle,
          opacity: (!isViewMode && !String(formData?.inn || '').trim() && name !== 'inn') ? 0.62 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        <label style={labelStyle}>
          {label}
          {required && <span style={{ color: '#e74c3c' }}>*</span>}
        </label>
        
        {type === 'select' ? (
          <select
            value={formData[name] || ''}
            onChange={(e) => updateFormData(name, e.target.value)}
            style={{
              ...inputStyle,
              ...(name === 'legal_form'
                ? {
                    height: '43px',
                    boxSizing: 'border-box',
                    padding: '8px 12px',
                    lineHeight: '18px',
                  }
                : {}),
            }}
            required={required}
            disabled={isViewMode || disabled}
          >
            {options.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === 'textarea' ? (
          <textarea
            value={formData[name] || ''}
            onChange={(e) => updateFormData(name, e.target.value)}
            style={{ ...inputStyle, minHeight: rows ? `${rows * 20 + 24}px` : '80px', resize: 'vertical' }}
            placeholder={placeholder}
            required={required}
            disabled={isViewMode || disabled}
          />
        ) : (
          <input
            type={type}
            value={formData[name] || ''}
            onChange={(!isViewMode && name === 'phone') ? handlePhoneChange : (!isViewMode && name === 'snils') ? handleSnilsChange : (e) => updateFormData(name, e.target.value)}
            ref={!isViewMode && name === 'phone' ? phoneInputRef : (!isViewMode && name === 'snils' ? snilsInputRef : undefined)}
            inputMode={name === 'phone' ? 'tel' : (name === 'snils' ? 'numeric' : undefined)}
            autoComplete={name === 'phone' ? 'tel' : undefined}
            style={inputStyle}
            placeholder={placeholder}
            required={required}
            disabled={isViewMode || disabled}
          />
        )}
        
        {error && <span style={errorStyle}>{error}</span>}
      </div>
    );
  };

  const entityType = isManager
    ? 'individual'
    : (formData?.entity_type || (formData?.type === 'arbitrage_manager' ? 'individual' : 'legal'));
  const isLegalEntity = entityType === 'legal';
  const isEntrepreneur = entityType === 'entrepreneur';

  useEffect(() => {
    if (isManager) return;
    if (isEntrepreneur && formData?.legal_form !== 'ИП') {
      updateFormData('legal_form', 'ИП');
      return;
    }
    if (!isLegalEntity && !isEntrepreneur && formData?.legal_form) {
      updateFormData('legal_form', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManager, isEntrepreneur, isLegalEntity, formData?.legal_form]);

  // Если у карточки уже есть сохранённый снимок СРО — пробуем только выбрать пункт в селекте, не перетирая поля.
  useEffect(() => {
    if (!Array.isArray(sros) || sros.length === 0) return;
    if (selectedSroId) return;
    const inn = (formData?.sro_inn || '').trim();
    const ogrn = (formData?.sro_ogrn || '').trim();
    const name = (formData?.sro_name || '').trim();
    if (!inn && !ogrn && !name) return;
    const normalizeName = (value) => (value || '')
      .toLowerCase()
      .replace(/ё/g, 'е')
      .replace(/[«»"]/g, '')
      .replace(/[^\p{L}\p{N}\s]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    const normalizeShort = (value) => normalizeName(value)
      .replace(/\b(сро|саморегулируемая|организация|ассоциация)\b/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const toAcronym = (value) => {
      const tokens = normalizeName(value).split(' ').filter(Boolean);
      return tokens.map((word) => word[0]).join('');
    };
    const compact = (value) => normalizeName(value).replace(/\s+/g, '');
    const candidates = sros.filter((x) => {
      if (inn && String(x.inn || '').trim() !== inn) return false;
      if (ogrn && String(x.ogrn || '').trim() !== ogrn) return false;
      if (name) {
        const left = normalizeName(x.name);
        const right = normalizeName(name);
        if (left === right) return true;
        const leftShort = normalizeShort(x.name);
        const rightShort = normalizeShort(name);
        if (leftShort && rightShort && leftShort === rightShort) return true;
        const leftAcr = toAcronym(x.name);
        const rightCmp = compact(name);
        if (leftAcr && rightCmp && leftAcr === rightCmp) return true;
        return false;
      }
      return true;
    });
    if (candidates.length === 1) {
      setSelectedSroId(candidates[0].id);
    }
  }, [sros, formData?.sro_inn, formData?.sro_ogrn, formData?.sro_name, selectedSroId]);

  return (
    <form
      onSubmit={handleSubmit}
      onChangeCapture={() => {
        dismissInnGuide();
      }}
      onInputCapture={dismissInnGuide}
      onMouseDownCapture={dismissInnGuide}
      onKeyDownCapture={dismissInnGuide}
      style={{ position: 'relative' }}
    >
      <Toast toast={innGuideToast} />
      {!!organizer && (
        <div style={{ marginBottom: 14 }}>
          <OwnerBadge email={organizer.owner_email} />
        </div>
      )}
      {/* Новый тип организатора (ЮЛ/ИП/ФЛ) — только в карточке Организаторов */}
      {!isManager && renderField({
        label: 'Тип организатора',
        name: 'entity_type',
        type: 'select',
        required: true,
        options: [
          { value: 'legal', label: 'Юридическое лицо' },
          { value: 'entrepreneur', label: 'Индивидуальный предприниматель' },
          { value: 'individual', label: 'Физическое лицо' },
        ]
      })}

      {!isManager && (isLegalEntity || isEntrepreneur) ? (
        <div style={{ display: 'grid', gridTemplateColumns: '0.7fr 2.3fr', gap: '16px' }}>
          {isLegalEntity
            ? renderField({
                label: 'Организационно-правовая форма',
                name: 'legal_form',
                type: 'select',
                options: [
                  { value: '', label: '—' },
                  { value: 'ООО', label: 'ООО' },
                  { value: 'АО', label: 'АО' },
                  { value: 'ПАО', label: 'ПАО' },
                  { value: 'НАО', label: 'НАО' },
                  { value: 'ОАО', label: 'ОАО' },
                  { value: 'ЗАО', label: 'ЗАО' },
                  { value: 'ПАО/ОАО', label: 'ПАО/ОАО' },
                  { value: 'ГУП', label: 'ГУП' },
                  { value: 'МУП', label: 'МУП' },
                  { value: 'ФГУП', label: 'ФГУП' },
                  { value: 'ГБУ', label: 'ГБУ' },
                  { value: 'МБУ', label: 'МБУ' },
                  { value: 'ПК', label: 'ПК' },
                  { value: 'ЖСК', label: 'ЖСК' },
                  { value: 'ТСЖ', label: 'ТСЖ' },
                  { value: 'НКО', label: 'НКО' },
                  { value: 'АНО', label: 'АНО' },
                  { value: 'Фонд', label: 'Фонд' },
                  { value: 'Ассоциация', label: 'Ассоциация' },
                  { value: 'Союз', label: 'Союз' }
                ],
              })
            : renderField({
                label: 'Организационно-правовая форма',
                name: 'legal_form',
                disabled: true,
              })}

          {renderField({
            label: isLegalEntity ? 'Название организации' : 'ФИО организатора',
            name: 'name',
            required: true,
            placeholder: isLegalEntity ? 'Краткое название организации' : 'Иванов Иван Иванович',
            autoFilled: autoFilledFields.has('name')
          })}
        </div>
      ) : (
        renderField({
          label: isManager ? 'ФИО' : 'ФИО организатора',
          name: 'name',
          required: true,
          placeholder: 'Иванов Иван Иванович',
          autoFilled: autoFilledFields.has('name')
        })
      )}

      {/* ЕИО под названием — только для Организаторов */}
      {!isManager && isLegalEntity && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px', marginBottom: '6px' }}>
            {renderField({ label: 'ЕИО — должность', name: 'executive_role', placeholder: 'Генеральный директор', autoFilled: autoFilledFields.has('executive_role') })}
            {renderField({ label: 'ЕИО — ФИО', name: 'executive_name', placeholder: 'Иванов Иван Иванович', autoFilled: autoFilledFields.has('executive_name') })}
          </div>
          <div style={{ color: '#6c757d', fontSize: '12px', marginTop: '-8px', marginBottom: '14px' }}>
            ЕИО — единоличный исполнительный орган организации (например, генеральный директор).
          </div>
        </>
      )}

      {fixedType ? (
        <input type="hidden" value={fixedType} />
      ) : (
        renderField({
          label: 'Категория организатора',
          name: 'type',
          type: 'select',
          required: true,
          options: [
            { value: 'arbitrage_manager', label: 'Арбитражный управляющий' },
            { value: 'specialized_org', label: 'Специализированная организация' }
          ]
        })
      )}

      {isManager ? (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#2c3e50', fontSize: '14px' }}>
            ИНН
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
            <input
              type="text"
              value={formData?.inn || ''}
              onChange={(e) => updateFormData('inn', e.target.value)}
              ref={!isViewMode ? innInputRef : undefined}
              style={{
                width: '100%',
                padding: '12px',
                border: validationErrors.inn ? '2px solid #e74c3c' : '1px solid #ddd',
                borderColor: shouldHighlightInn ? '#4da3ff' : (validationErrors.inn ? '#e74c3c' : '#ddd'),
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit',
                backgroundColor: isViewMode ? '#f8f9fa' : 'white',
                color: isViewMode ? '#6c757d' : '#2c3e50',
                outline: 'none',
                transition: 'border-color 0.2s ease',
                boxShadow: shouldHighlightInn
                  ? '0 0 0 3px rgba(77, 163, 255, 0.18)'
                  : (autoFilledFields.has('inn') ? 'inset 4px 0 0 #1f6feb' : 'none')
              }}
              placeholder="1234567890"
              disabled={isViewMode}
            />
            {!isViewMode && (
              <div style={{ display: 'flex', gap: 8 }}>
                {!isViewMode && (
                  <button
                    type="button"
                    onClick={runFillByInn}
                    disabled={innFillStatus.loading}
                    style={{
                      height: 44,
                      padding: '0 14px',
                      borderRadius: 6,
                      border: '1px solid #0d6efd',
                      background: innFillStatus.loading ? '#cfe2ff' : '#0d6efd',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: innFillStatus.loading ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      boxShadow: innFillStatus.loading ? 'none' : '0 2px 6px rgba(13, 110, 253, 0.25)',
                    }}
                  >
                    {innFillStatus.loading ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 50 50"
                          role="img"
                          aria-label="Загрузка"
                        >
                          <circle
                            cx="25"
                            cy="25"
                            r="20"
                            fill="none"
                            stroke="#6c757d"
                            strokeWidth="5"
                            strokeLinecap="round"
                            strokeDasharray="90 60"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 25 25"
                              to="360 25 25"
                              dur="0.9s"
                              repeatCount="indefinite"
                            />
                          </circle>
                        </svg>
                        Заполнение…
                      </span>
                    ) : (
                      'Заполнить по ИНН'
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
          {validationErrors.inn && <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>{validationErrors.inn}</span>}
          {!!innFillStatus.error && (
            <div style={{ color: '#e74c3c', fontSize: 12, marginTop: 6 }}>
              {innFillStatus.error}
            </div>
          )}
          {!!innFillNotice && (
            <div style={{ color: '#8a6d1d', fontSize: 12, marginTop: 6 }}>
              {innFillNotice}
            </div>
          )}
        </div>
      ) : (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#2c3e50', fontSize: '14px' }}>
            ИНН
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center', position: 'relative' }}>
            <input
              type="text"
              value={formData?.inn || ''}
              onChange={(e) => updateFormData('inn', e.target.value)}
              ref={!isViewMode ? innInputRef : undefined}
              style={{
                width: '100%',
                padding: '12px',
                border: validationErrors.inn ? '2px solid #e74c3c' : '1px solid #ddd',
                borderColor: shouldHighlightInn ? '#4da3ff' : (validationErrors.inn ? '#e74c3c' : '#ddd'),
                borderRadius: '6px',
                fontSize: '14px',
                fontFamily: 'inherit',
                backgroundColor: isViewMode ? '#f8f9fa' : 'white',
                color: isViewMode ? '#6c757d' : '#2c3e50',
                outline: 'none',
                transition: 'border-color 0.2s ease',
                boxShadow: shouldHighlightInn
                  ? '0 0 0 3px rgba(77, 163, 255, 0.18)'
                  : (autoFilledFields.has('inn') ? 'inset 4px 0 0 #1f6feb' : 'none')
              }}
              placeholder="1234567890"
              disabled={isViewMode}
            />
            {!isViewMode && (
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => runFillByInn()}
                    disabled={innFillStatus.loading}
                    style={{
                      height: 44,
                      padding: '0 14px',
                      borderRadius: 6,
                      border: '1px solid #0d6efd',
                      background: innFillStatus.loading ? '#cfe2ff' : '#0d6efd',
                      color: '#fff',
                      fontWeight: 600,
                      cursor: innFillStatus.loading ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      boxShadow: innFillStatus.loading ? 'none' : '0 2px 6px rgba(13, 110, 253, 0.25)',
                    }}
                  >
                    {innFillStatus.loading ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 50 50"
                          role="img"
                          aria-label="Загрузка"
                        >
                          <circle
                            cx="25"
                            cy="25"
                            r="20"
                            fill="none"
                            stroke="#6c757d"
                            strokeWidth="5"
                            strokeLinecap="round"
                            strokeDasharray="90 60"
                          >
                            <animateTransform
                              attributeName="transform"
                              type="rotate"
                              from="0 25 25"
                              to="360 25 25"
                              dur="0.9s"
                              repeatCount="indefinite"
                            />
                          </circle>
                        </svg>
                        Заполнение…
                      </span>
                    ) : (
                      'Заполнить по ИНН'
                    )}
                  </button>
                </div>
                {innTypePrompt.open && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: 0,
                      marginTop: 6,
                      background: '#fff',
                      border: '1px solid #dee2e6',
                      borderRadius: 8,
                      padding: '10px',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
                      zIndex: 10,
                      minWidth: 220
                    }}
                  >
                    <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 8 }}>
                      Для кого выполнить заполнение?
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        type="button"
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          borderRadius: 6,
                          border: '1px solid #ced4da',
                          background: '#f8f9fa',
                          fontSize: 12,
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          setInnTypePrompt({ open: false, inn: '' });
                          setFormData((prev) => ({ ...(prev || {}), entity_type: 'individual' }));
                          await runFillByInn('individual');
                        }}
                      >
                        Физлицо
                      </button>
                      <button
                        type="button"
                        style={{
                          flex: 1,
                          padding: '6px 8px',
                          borderRadius: 6,
                          border: '1px solid #ced4da',
                          background: '#f8f9fa',
                          fontSize: 12,
                          cursor: 'pointer'
                        }}
                        onClick={async () => {
                          setInnTypePrompt({ open: false, inn: '' });
                          setFormData((prev) => ({ ...(prev || {}), entity_type: 'entrepreneur' }));
                          await runFillByInn('entrepreneur');
                        }}
                      >
                        ИП
                      </button>
                    </div>
                    <button
                      type="button"
                      style={{
                        marginTop: 8,
                        width: '100%',
                        padding: '6px 8px',
                        borderRadius: 6,
                        border: '1px solid transparent',
                        background: 'transparent',
                        fontSize: 12,
                        color: '#6c757d',
                        cursor: 'pointer'
                      }}
                      onClick={() => setInnTypePrompt({ open: false, inn: '' })}
                    >
                      Отмена
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          {validationErrors.inn && (
            <span style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', display: 'block' }}>
              {validationErrors.inn}
            </span>
          )}
          {!!innFillStatus.error && (
            <div style={{ color: '#e74c3c', fontSize: 12, marginTop: 6 }}>
              {innFillStatus.error}
            </div>
          )}
          {!!innFillNotice && (
            <div style={{ color: '#8a6d1d', fontSize: 12, marginTop: 6 }}>
              {innFillNotice}
            </div>
          )}
        </div>
      )}
      {isManager && renderField({ label: 'СНИЛС', name: 'snils', placeholder: 'XXX-XXX-XXX XX', autoFilled: autoFilledFields.has('snils') })}

      {!isManager && (isLegalEntity || isEntrepreneur) && renderField({ label: 'ОГРН/ОГРНИП', name: 'ogrn', placeholder: isEntrepreneur ? '315774600000000' : '1027700132195', autoFilled: autoFilledFields.has('ogrn') })}
      {!isManager && isLegalEntity && renderField({ label: 'КПП', name: 'kpp', placeholder: '770301001', autoFilled: autoFilledFields.has('kpp') })}

      {!isManager && isLegalEntity && renderField({ label: 'Место нахождения (адрес)', name: 'address', placeholder: 'Адрес фактического места нахождения организации', autoFilled: autoFilledFields.has('address') })}

      {/* Контактные данные — выделенный блок */}
      <div style={{ marginTop: '12px', marginBottom: '8px', padding: '14px', border: '1px solid #e1e5e9', borderRadius: '6px', background: '#f8f9fa' }}>
        <div style={{ fontWeight: 600, color: '#2c3e50', marginBottom: '10px' }}>Контактные данные</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          {renderField({ label: 'Телефон', name: 'phone', placeholder: '+7 (___) ___-__-__', autoFilled: autoFilledFields.has('phone') })}
          {renderField({ label: 'Email', name: 'email', placeholder: 'info@example.com', type: 'email', autoFilled: autoFilledFields.has('email') })}
        </div>
        {renderField({ label: 'Адрес для корреспонденции', name: 'mailing_address', placeholder: 'Почтовый адрес', autoFilled: autoFilledFields.has('mailing_address') })}
      </div>

      {/* Сводная контактная информация (старое поле) скрыта */}
      {/* renderField({ label: 'Контактная информация (сводно)', name: 'contacts', type: 'textarea', rows: 3 }) */}

      {isManager && (
        <>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#2c3e50', fontSize: '14px' }}>
              СРО (выбор из справочника)
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
              <div style={{ minWidth: 0, boxShadow: autoFilledFields.has('sro_select') ? 'inset 4px 0 0 #1f6feb' : 'none', borderRadius: 6 }}>
                <SearchableSelect
                  value={selectedSroId}
                  onChange={handleSelectSro}
                  options={sros}
                  placeholder={srosLoading ? 'Загрузка списка СРО…' : 'Выберите СРО'}
                  searchPlaceholder="Поиск СРО..."
                  displayKey="name"
                  valueKey="id"
                  disabled={isViewMode || srosLoading}
                  formatOption={(sro) => {
                    const shortName = (sro?.short_name || '').trim();
                    const baseName = sro?.name || '';
                    const main = shortName ? `${shortName} / ${baseName}` : baseName;
                    const inn = sro?.inn ? `ИНН ${sro.inn}` : null;
                    const ogrn = sro?.ogrn ? `ОГРН ${sro.ogrn}` : null;
                    const tail = [inn, ogrn].filter(Boolean).join(', ');
                    return tail ? `${main} (${tail})` : main;
                  }}
                  formatSelected={(sro) => {
                    const shortName = (sro?.short_name || '').trim();
                    const baseName = sro?.name || '';
                    return shortName ? `${shortName} / ${baseName}` : baseName;
                  }}
                />
              </div>
              {!isViewMode && (
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSroId('');
                    setFormData(prev => ({ ...(prev || {}), sro_name: '', sro_inn: '', sro_ogrn: '', sro_address: '', sro_info: '' }));
                  }}
                  disabled={srosLoading}
                  style={{
                    padding: '10px 14px',
                    background: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: 8,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                  title="Очистить данные СРО в карточке"
                >
                  Очистить
                </button>
              )}
            </div>
            <div style={{ color: '#6c757d', fontSize: 12, marginTop: 6 }}>
              При выборе СРО данные копируются в карточку (без связи со справочником).
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {renderField({ label: 'СРО — наименование', name: 'sro_name', placeholder: 'Наименование СРО', marginBottom: 12, autoFilled: autoFilledFields.has('sro_name') })}
            {renderField({ label: 'СРО — ИНН', name: 'sro_inn', placeholder: 'ИНН СРО', marginBottom: 12, autoFilled: autoFilledFields.has('sro_inn') })}
            {renderField({ label: 'СРО — ОГРН', name: 'sro_ogrn', placeholder: 'ОГРН СРО', marginBottom: 12, autoFilled: autoFilledFields.has('sro_ogrn') })}
            <div style={{ gridColumn: '1 / -1' }}>
              {renderField({ label: 'СРО — адрес', name: 'sro_address', type: 'textarea', rows: 2, placeholder: 'Адрес СРО (по Росреестру)', marginBottom: 12, autoFilled: autoFilledFields.has('sro_address') })}
            </div>
          </div>
        </>
      )}

      {!isViewMode && (
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e1e5e9'
        }}>
          {autoFilledFields.size > 0 && (
            <div style={{ marginRight: 'auto', fontSize: '12px', color: '#6c757d', alignSelf: 'center' }}>
              Поля с синей полосой слева заполнены автоматически. Метка снимается при ручном изменении.
            </div>
          )}
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            disabled={isSaving}
          >
            Отмена
          </button>
          <button
            type="button"
            onClick={() => runSave(false).catch(() => {})}
            style={{
              padding: '12px 24px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            disabled={isSaving}
          >
            {isSaving ? 'Сохранение...' : 'Сохранить'}
          </button>
          <button
            type="button"
            onClick={() => runSave(true).catch(() => {})}
            style={{
              padding: '12px 24px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            disabled={isSaving}
          >
            {isSaving ? 'Сохранение...' : 'Сохранить и выйти'}
          </button>
        </div>
      )}
      
      {isViewMode && (
        <div style={{ 
          display: 'flex', 
          gap: '12px', 
          justifyContent: 'flex-end',
          marginTop: '32px',
          paddingTop: '24px',
          borderTop: '1px solid #e1e5e9'
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '12px 24px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
          >
            Закрыть
          </button>
        </div>
      )}
      {showInnFillProgress && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}
        >
          <div
            style={{
              width: 420,
              maxWidth: 'calc(100% - 32px)',
              background: '#fff',
              borderRadius: 12,
              padding: '20px 22px',
              boxShadow: '0 12px 30px rgba(0,0,0,0.18)'
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
              {isManager ? 'Заполнение управляющего по ИНН' : 'Заполнение организатора по ИНН'}
            </div>
            <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 8 }}>
              Идет заполнение… {innFillProgress}%
            </div>
            <div style={{ fontSize: 12, color: '#6c757d', marginBottom: 10 }}>
              Статус: {innFillStage || 'Подготовка'}
            </div>
            <div style={{ height: 8, background: '#e9ecef', borderRadius: 999 }}>
              <div
                style={{
                  height: '100%',
                  width: `${innFillProgress}%`,
                  background: '#2f80ed',
                  borderRadius: 999,
                  transition: 'width 0.35s ease'
                }}
              />
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: '#6c757d' }}>
              Это может занять до минуты.
            </div>
          </div>
        </div>
      )}
    </form>
  );
};

export default OrganizerForm;
